import { FC, useCallback, useMemo, useState } from 'react';

import { UserSubscriptionTypesEnum } from '~/common/enums/user-subscription-types.enum';

import { PromptItem } from '../PromptItem';
import { PromptWindowedGrid } from '../PromptWindowedGrid';
import { DeletePromptConfirmation } from '../DeletePromptConfirmation';
import { PromptList } from '../../pages/list/styles';
import { IPromptListItem } from '../../interfaces/i-prompt-list-item';

const ENABLE_PROMPT_GRID_WINDOWING = true;
const PROMPT_GRID_WINDOWING_THRESHOLD = 24;

interface IUserPromptListProps {
  prompts: IPromptListItem[];
  togglePromptApproval: (prompt: IPromptListItem) => Promise<void>;
  handleOpenForbiddenAccessModal: (type: UserSubscriptionTypesEnum) => void;
  editPrompt: (promptId: string) => void;
  deletePrompt: (promptId: string) => Promise<void>;
}
export const UserPromptList: FC<IUserPromptListProps> = ({
  prompts,
  togglePromptApproval,
  handleOpenForbiddenAccessModal,
  editPrompt,
  deletePrompt,
}) => {
  const [deletingPromptId, setDeletingPromptId] = useState<string | null>(null);
  const deletingPromptTitle = useMemo(
    () => prompts.find((where) => where.id === deletingPromptId)?.title,
    [deletingPromptId, prompts]
  );
  const shouldUseWindowing =
    ENABLE_PROMPT_GRID_WINDOWING &&
    prompts.length >= PROMPT_GRID_WINDOWING_THRESHOLD;

  const renderPromptItem = useCallback(
    (item: IPromptListItem) => (
      <PromptItem
        key={item.id ?? item.title}
        togglePromptApproval={togglePromptApproval}
        handleOpenForbiddenAccessModal={handleOpenForbiddenAccessModal}
        onEditPrompt={editPrompt}
        onDeletePrompt={setDeletingPromptId}
        prompt={item}
      />
    ),
    [
      editPrompt,
      handleOpenForbiddenAccessModal,
      togglePromptApproval,
    ]
  );

  return (
    <>
      <DeletePromptConfirmation
        open={deletingPromptId !== null}
        close={() => setDeletingPromptId(null)}
        title={deletingPromptTitle}
        confirm={async () => {
          if (!deletingPromptId) return;
          await deletePrompt(deletingPromptId).finally(() =>
            setDeletingPromptId(null)
          );
        }}
      />

      {shouldUseWindowing ? (
        <PromptWindowedGrid
          items={prompts}
          renderItem={renderPromptItem}
        />
      ) : (
        <PromptList>{prompts.map(renderPromptItem)}</PromptList>
      )}
    </>
  );
};
